/**
 * Détection du mode d'exécution et nettoyage du Service Worker.
 *
 * Stratégie : le Service Worker n'est utile QUE pour l'app installée (PWA
 * standalone, offline). En navigateur classique, il interceptait les
 * navigations et servait l'`index.html` précaché (donc l'ancienne version)
 * après un déploiement — obligeant à vider le cache. On ne l'enregistre donc
 * qu'en mode standalone, et on désenregistre tout SW résiduel côté navigateur
 * pour que nginx serve toujours la dernière version sur une simple actualisation.
 */

/** True si l'app tourne comme PWA installée (standalone), pas dans un onglet. */
export function isRunningStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const byDisplayMode =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches;
  // iOS Safari : pas de display-mode, expose navigator.standalone.
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return byDisplayMode || iosStandalone;
}

/**
 * En navigateur classique (hors PWA installée) : désenregistre tout Service
 * Worker existant et vide ses caches, afin que les utilisateurs déjà « coincés »
 * sur un ancien SW soient soignés et qu'une simple actualisation charge la
 * dernière version. Best-effort — ne bloque jamais le démarrage de l'app.
 */
export async function unregisterServiceWorkerInBrowser(): Promise<void> {
  if (typeof window === 'undefined' || isRunningStandalone()) return;
  if (!('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Silencieux : un échec de nettoyage ne doit pas empêcher l'app de tourner.
  }
}

/** Retire seulement les anciennes réponses commerciales, y compris en PWA. */
export async function purgeStorefrontResponses(): Promise<void> {
  if (typeof window === 'undefined' || !('caches' in window)) return;
  try {
    for (const name of await caches.keys()) {
      const cache = await caches.open(name);
      for (const request of await cache.keys()) {
        const path = new URL(request.url).pathname;
        if (
          path.startsWith('/api/v1/storefront/') ||
          /^\/shop(?:\/|$)/.test(path) ||
          /^\/storefront-(?:document|sitemap|directory-sitemap|llms)/.test(path)
        ) {
          await cache.delete(request);
        }
      }
    }
  } catch {
    // Browsers can deny CacheStorage; HTTP and fetch still use no-store.
  }
}
